import React, { FC, ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { renderChildren } from '@fiverr-private/ui_utils';
import { theme } from '@fiverr-private/theme';
import {
    DEFAULT_TIMEOUT,
    VERTICAL_DATA_ATTRIBUTE,
    VERTICAL_VALUES,
    HORIZONTAL_DATA_ATTRIBUTE,
    HORIZONTAL_VALUES,
    TOAST_MARGIN,
    NO_CONTENT_ERROR,
    INVALID_CONTENT_TYPE_ERROR,
    BASE_TOAST_STYLE,
} from './constants';
import * as toast from './toast';
import { BASE_CLASS as BASE_TOAST_CLASS } from './toast/constants';
import ToastContainer from './ToastContainer';
import { buildContainerClass } from './ToastContainer/constants';
import { LocationsType, ToasterOptionsType } from './types';

/**
 * A memoized collection of all trays.
 */
const trays: { [key: string]: any } = {};

/**
 * Uses the latest container class on the container
 */
const adjustContainerClass = (location: string) => {
    const containerClassName = buildContainerClass(location);
    const container = document.querySelector(`.${containerClassName}`);

    if (container) {
        container.className = containerClassName;
    }
};

/**
 * Ensures a toast tray exists, and creates it if not.
 */
const ensureContainer = (location: LocationsType) => {
    // Save some DOM querying
    if (trays[location]) {
        adjustContainerClass(location);
        return;
    }

    const existingTray = document.querySelector(`.${buildContainerClass(location)} ul`);

    if (existingTray) {
        adjustContainerClass(location);
        trays[location] = existingTray; // Make sure this bundle's trays object is populated
        return;
    }

    const container = new ToastContainer(location);
    document.body.appendChild(container.element);

    trays[location] = container.tray;
};

/**
 * Reorders the toasts inside a given tray element, animating them in the process.
 */
const reorderToasts = (tray: HTMLElement) => {
    const isBottomTray = tray.getAttribute(VERTICAL_DATA_ATTRIBUTE) === VERTICAL_VALUES.BOTTOM;
    const isCenterTray = tray.getAttribute(HORIZONTAL_DATA_ATTRIBUTE) === HORIZONTAL_VALUES.CENTER;
    const direction = isBottomTray ? -1 : 1;
    let totalMargin = TOAST_MARGIN * direction;

    const toasts: (Element & { style: { [key: string]: string } })[] = Array.from(
        tray.querySelectorAll(`.${BASE_TOAST_CLASS}`)
    );

    toasts.reverse().forEach((toastElement, index) => {
        const toastHeight = toastElement.scrollHeight;
        Object.assign(toastElement.style, {
            transitionDelay: `${0.02 * index}s`,
            transform: [`translateY(${totalMargin}px)`, isCenterTray && 'translateX(-50%)'].filter(Boolean).join(' '),
            opacity: theme.opacity['1'],
        });
        totalMargin += (toastHeight + TOAST_MARGIN) * direction;
    });
};

/**
 * Adds a toast to a tray and "pushes" it - rendering it in the tray whilst pushing previous toasts.
 */
const pushToast = (location: LocationsType, content: ReactNode | FC, duration: number) => {
    const toastElement = toast.create(duration);
    const tray = trays[location];
    const isBottomTray = tray.getAttribute(VERTICAL_DATA_ATTRIBUTE) === VERTICAL_VALUES.BOTTOM;
    const isCenterTray = tray.getAttribute(HORIZONTAL_DATA_ATTRIBUTE) === HORIZONTAL_VALUES.CENTER;
    const direction = isBottomTray ? 1 : -1;

    Object.assign(toastElement.style, {
        ...BASE_TOAST_STYLE,
        transform: [`translateY(${100 * direction}%)`, isCenterTray && 'translateX(-50%)'].filter(Boolean).join(' '),
        ...toast.getStyle(location),
    });

    ReactDOM.render(renderChildren(content), toastElement);

    trays[location].appendChild(toastElement);

    reorderToasts(trays[location]);
    if (duration !== Infinity) {
        toast.scheduleRemoval(toastElement, {
            duration,
        });
    }
};

/**
 * "Raises" a toast, i.e. adds it to a tray and renders it.
 */
const raise = (
    content: ReactNode,
    { location = 'bottom-end', duration = DEFAULT_TIMEOUT }: ToasterOptionsType = {}
) => {
    if (typeof document === 'undefined') {
        return;
    }

    if (!content) {
        throw new Error(NO_CONTENT_ERROR);
    }

    if (!React.isValidElement(content)) {
        throw new Error(INVALID_CONTENT_TYPE_ERROR);
    }

    ensureContainer(location);
    pushToast(location, content, duration);
};

export default raise;
